/* screens/auth/SignUp.jsx — Регистрация (S12, /signup).
   Имя+Email+Пароль (StrengthMeter), согласие с офертой/политикой (ЖИВЫЕ ссылки — фикс №21),
   кнопка disabled пока agree=false. Успех → /email-sent (mode=verify, email в state).
   Занятый ПОДТВЕРЖДЁННЫЙ email: бэк отвечает 409 code='email_exists' — показываем
   сообщение «уже зарегистрирован» и ссылку «Войти» (anti-enumeration снята только для
   готовых аккаунтов; занятый НЕподтверждённый email по-прежнему 201 + повторный verify,
   эта ветка тогда не наступает). 400-валидация полей (email/пароль/имя) → ошибка под
   полем (err.field из unwrap); 429 rate_limited / прочее → topError.
   Хэндофф с демо-стенда (/business-demo, CJM): анкета кладёт {token,email,name} в
   sessionStorage (DEMO_SIGNUP_STORAGE_KEY) — префиллим имя/email (редактируемы), шлём
   demo_signup_token в register; ответ auto_login=true → сессия уже поднята: login(user)
   как в SignIn и сразу на главную, мимо /email-sent. 409 email_exists ключ НЕ чистит —
   SignIn привяжет онбординг-флоу по токену после входа.
   Register зовём напрямую (authApi+unwrap, мимо authReal): адаптер собирает body
   явными полями и уронил бы demo_signup_token; throw-контракт catch-ветки сохранён. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, PasswordInput, StrengthMeter, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { ScreenHeader } from '../../components/ui/Metric.jsx';
import * as authApi from '../../lib/api/auth.js';
import { DEMO_SIGNUP_STORAGE_KEY } from '../../lib/api/demo.js';
import { unwrap } from '../../lib/api/unwrap.js';
import { parseDemoSignupStorage } from '../../lib/demoLead.js';
import { RR } from '../../lib/router.jsx';
import { safeNext } from '../../lib/safeNext.js';

  const { useState, useEffect } = React;
  const { useNavigate, useSearchParams, Link } = RR;
  const PENDING_INVITE_KEY = 'pending_invite_token';
  const PENDING_PAY_LINK_KEY = 'pending_payment_link_token';
  const PENDING_GIFT_KEY = 'pending_gift_code';
  // Куда вернуть юзера после подтверждения email + входа (лендинг курса, фикс №16/A11).
  // Тот же ключ, что читает SignIn — переживает раунд /email-sent → /signin.
  const ACADEMY_NEXT_KEY = 'restorium.academy.next';

  // Куда вернуть человека, если он пришёл регистрироваться С приглашения, с именной
  // ссылки на оплату или с лендинга курса: токен/путь живёт только в sessionStorage,
  // и без возврата ссылка теряется. Актуально ТОЛЬКО для ветки auto_login — остальные
  // ведут на /email-sent → verify → /signin, где возврат делает SignIn.
  function pendingReturnPath() {
    // sessionStorage бросает SecurityError, когда для origin заблокированы данные
    // сайта. Без защиты исключение ушло бы во внешний catch submit'а, и уже
    // зарегистрированный и залогиненный человек увидел бы ошибку формы вместо перехода.
    try {
      const invite = sessionStorage.getItem(PENDING_INVITE_KEY);
      if (invite) { sessionStorage.removeItem(PENDING_INVITE_KEY); return '/invite/accept/' + invite; }
      const pay = sessionStorage.getItem(PENDING_PAY_LINK_KEY);
      if (pay) { sessionStorage.removeItem(PENDING_PAY_LINK_KEY); return '/pay/' + pay; }
      const gift = sessionStorage.getItem(PENDING_GIFT_KEY);
      if (gift) { sessionStorage.removeItem(PENDING_GIFT_KEY); return '/gift/' + gift; }
      const next = safeNext(sessionStorage.getItem(ACADEMY_NEXT_KEY));
      if (next) { sessionStorage.removeItem(ACADEMY_NEXT_KEY); return next; }
    } catch { /* приватный режим — просто уводим на главную */ }
    return null;
  }

  function SignUp() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [sp] = useSearchParams();
    // Валидный локальный next кладём в sessionStorage на маунте: SignUp уводит на /email-sent,
    // подтверждение приводит на /signin, который и заберёт next из sessionStorage.
    const nextFromUrl = safeNext(sp.get('next'));
    // Подарочный код из письма: /signup?gift=XXXX-XXXX-XXXX. Кладём в
    // sessionStorage тем же приёмом, что и next — подтверждение почты уводит
    // на /signin, и без сохранения код потерялся бы на этом круге.
    const giftFromUrl = (sp.get('gift') || '').trim();
    useEffect(() => {
      if (nextFromUrl) {
        try { sessionStorage.setItem(ACADEMY_NEXT_KEY, nextFromUrl); } catch (e) {}
      }
      if (giftFromUrl) {
        try { sessionStorage.setItem(PENDING_GIFT_KEY, giftFromUrl); } catch (e) {}
      }
    }, [nextFromUrl, giftFromUrl]);
    // Заявка с демо-стенда (если есть): {token, email, name} — источник префилла.
    // Парс — чистая функция (lib/demoLead.js); try/catch только вокруг доступа к
    // sessionStorage (приватные режимы могут бросать на самом обращении).
    const [demo] = useState(() => {
      try { return parseDemoSignupStorage(sessionStorage.getItem(DEMO_SIGNUP_STORAGE_KEY)); }
      catch { return null; }
    });
    const [name, setName] = useState((demo && demo.name) || '');
    const [email, setEmail] = useState((demo && demo.email) || '');
    const [password, setPassword] = useState('');
    const [agree, setAgree] = useState(false);
    const [errors, setErrors] = useState({});
    const [topError, setTopError] = useState(null);
    // Занятый (подтверждённый) email — показываем в topError-callout ещё и ссылку «Войти».
    const [emailTaken, setEmailTaken] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
      e.preventDefault();
      const errs = {};
      if (!name.trim()) errs.name = 'Укажите имя.';
      // dot-atom как у бэка (DRF EmailField): точка не в начале/конце и без «..» по обе стороны @ —
      // иначе адрес внешне похож на валидный (напр. «foo.@gmail.com»), но бэк вернёт 400.
      if (!/^[^\s@.]+(?:\.[^\s@.]+)*@[^\s@.]+(?:\.[^\s@.]+)+$/.test(email.trim()))
        errs.email = 'Введите корректный email.';
      if (password.length < 8) errs.password = 'Минимум 8 символов.';
      setErrors(errs); setTopError(null); setEmailTaken(false);
      if (Object.keys(errs).length || !agree) return;

      setLoading(true);
      try {
        // name→full_name (контракт бэка); токен заявки демо-стенда — если есть.
        const body = { full_name: name, email, password, consent: agree };
        if (demo && demo.token) body.demo_signup_token = demo.token;
        const data = await unwrap(authApi.register(body));
        // CJM демо-стенда: бэк уже поднял сессию (auto_login) — входим тем же путём,
        // что SignIn (login(me-payload) грузит workspaces), и ведём на главную мимо /email-sent.
        if (data && data.auto_login && data.user) {
          try { sessionStorage.removeItem(DEMO_SIGNUP_STORAGE_KEY); } catch { /* noop */ }
          await login(data.user);
          navigate(pendingReturnPath() || '/', { replace: true });
          return;
        }
        navigate('/email-sent', { state: { email, mode: 'verify' } });
      } catch (err) {
        // Занятый подтверждённый email: бэк прямо сообщает об этом (409 code='email_exists').
        // Показываем сообщение и ссылку «Войти» (см. emailTaken-ветку в callout).
        // sessionStorage-ключ демо-заявки НАРОЧНО не чистим: SignIn после входа привяжет
        // онбординг-флоу по токену (createOnboardingFlow).
        if (err.code === 'email_exists') {
          setTopError(err.message || 'Аккаунт с этой почтой уже зарегистрирован. Войдите в систему.');
          setEmailTaken(true);
          setLoading(false);
          return;
        }
        // Ошибки валидации DRF приходят по полю (err.field='email'|'password'|'full_name') —
        // показываем их под нужным полем (бэковое full_name → форменное name); прочее
        // (rate_limit и т.п.) — общим topError.
        const fieldFor = { email: 'email', password: 'password', full_name: 'name', name: 'name' };
        const f = err.field && fieldFor[err.field];
        if (f) setErrors({ [f]: err.message });
        else setTopError(err.message || 'Не удалось создать аккаунт.');
        setLoading(false);
      }
    };

    return (
      <form className="auth-form stack stack-16" onSubmit={submit} noValidate>
        <ScreenHeader title="Регистрация" className="screen-header--accent" />

        {giftFromUrl && !topError && (
          <div className="callout callout--info" role="status">
            <Icon name="gift" size={16} />
            <span>
              Подарочный сертификат <b>{giftFromUrl}</b> сохранён. Создайте
              аккаунт — после подтверждения почты останется нажать «Активировать».
            </span>
          </div>
        )}

        {demo && !topError && (
          <div className="callout callout--info" role="status">
            <Icon name="info" size={16} />
            <span>Аккаунт для продолжения настройки — почта из вашей заявки. Осталось придумать пароль.</span>
          </div>
        )}

        {topError && (
          <div className="callout callout--bad" role="alert">
            <Icon name="alert-circle" size={16} />
            <div className="stack stack-6">
              <span>{topError}</span>
              {emailTaken && (
                <Link to="/signin" className="link">Войти</Link>
              )}
            </div>
          </div>
        )}

        <div className="stack stack-12">
          <Field label="Имя" htmlFor="su-name" error={errors.name}>
            <TextInput id="su-name" value={name} autoComplete="name" invalid={errors.name ? true : undefined}
              placeholder="Как к вам обращаться" onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="E-mail" htmlFor="su-email" error={errors.email}>
            <TextInput id="su-email" type="email" value={email} autoComplete="email" invalid={errors.email ? true : undefined}
              placeholder="you@restaurant.ru" onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Пароль" htmlFor="su-pass" error={errors.password}
            hint={!errors.password ? 'Минимум 8 символов' : undefined}>
            <PasswordInput id="su-pass" value={password} autoComplete="new-password" invalid={errors.password ? true : undefined}
              placeholder="Придумайте пароль" onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <StrengthMeter value={password} />
        </div>

        {/* Согласие — одно предложение с inline-ссылками. Бокс и «Я принимаю» (label htmlFor)
            тогглят чекбокс; <a> живут вне <label> → валидная разметка, ссылки кликаются (фикс №08). */}
        <div className="auth-form__agree">
          <input id="su-agree" type="checkbox" className="checkbox__input" checked={agree}
            aria-label="Я принимаю оферту и политику конфиденциальности"
            onChange={(e) => setAgree(e.target.checked)} />
          <label htmlFor="su-agree" className="checkbox__box" aria-hidden="true">
            <Icon name="check" size={12} />
          </label>
          <span className="auth-form__agree-text">
            <label htmlFor="su-agree" className="auth-form__agree-lead">Я принимаю</label>{' '}
            <Link to="/legal/offer" className="link">оферту</Link> и{' '}
            <Link to="/legal/privacy" className="link">политику конфиденциальности</Link>
          </span>
        </div>

        <Button type="submit" variant="primary" size="lg" loading={loading} disabled={!agree} className="full">
          Создать аккаунт
        </Button>

        <p className="auth-form__alt t-body text-dim">
          Уже есть аккаунт? <Link to={nextFromUrl ? '/signin?next=' + encodeURIComponent(nextFromUrl) : '/signin'} className="link">Войти</Link>
        </p>
      </form>
    );
  }

export { SignUp };
